import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getSchedulerHealth } from "api/schedulerHealthApi";
import { unwrapList } from "api/unwrap";

// Expected max staleness per scheduler (must match backend watchlist).
// Anything older than this is rendered red. Anything within 70% of threshold yellow.
const STALENESS_THRESHOLD_HOURS = {
  ride_overage_reporter: 48,
  plan_capacity_nudge: 30,
  trial_winback: 30,
  training_renewal_reminder: 30,
  document_expiry_reminder: 30,
  admin_compliance_digest: 30,
  driver_compliance_digest: 180,
  onboarding_stuck_state: 180,
};

const ageColor = (name, ageMs, status) => {
  if (status !== "ok") return "error";
  const thresholdH = STALENESS_THRESHOLD_HOURS[name] || 48;
  const ageH = ageMs / 3_600_000;
  if (ageH >= thresholdH) return "error";
  if (ageH >= thresholdH * 0.7) return "warning";
  return "success";
};

const formatAge = (ms) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h ago`;
};

const prettyMetadata = (raw) => {
  if (!raw) return "";
  try {
    const obj = JSON.parse(raw);
    return Object.entries(obj)
      .map(([k, v]) => `${k}=${v}`)
      .join(" · ");
  } catch {
    return raw;
  }
};

/**
 * Read-only view onto the scheduler_health table. Lets ops see at a glance
 * whether every cron is firing on time without grepping logs.
 *
 * Color logic:
 *   green   — last_run within 70% of threshold
 *   yellow  — 70-100% of threshold
 *   red     — past threshold OR last_status != 'ok'
 */
export const SchedulerHealthPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSchedulerHealth();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load scheduler health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000); // auto-refresh every minute
    return () => clearInterval(interval);
  }, [load]);

  const now = Date.now();

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Scheduler Health
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            One row per scheduled job. Auto-refreshes every minute. Red rows trigger an ops alert email after the watchlist threshold.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No scheduler heartbeats recorded yet. Wait for the first cron to fire (varies by scheduler — see watchlist for cadence).
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Scheduler</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last run</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last metadata</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const ageMs = now - r.lastRunAt;
                const color = ageColor(r.schedulerName, ageMs, r.lastStatus);
                return (
                  <TableRow key={r.schedulerName} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.schedulerName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.lastStatus} color={color} variant={color === "success" ? "outlined" : "filled"} sx={{ height: 22 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: color === "error" ? 700 : 400, color: color === "error" ? "#dc2626" : "inherit" }}>
                        {formatAge(ageMs)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(r.lastRunAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 480 }}>
                      <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                        {prettyMetadata(r.lastMetadata)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default SchedulerHealthPage;
